define((require) => {
    "use strict";

    const _          = require("underscore");
    const $          = require("jquery");
    const Marionette = require("marionette");
    const ViewScope  = require("shared/ui/common/ViewScope");
    const Browser    = require("shared/services/Browser");

    /**
     * An enhancement for Marionette.Region that can animate the views placed inside it.
     * Automatically monkey-patched into Marionette.
     *
     * The region/view lifecycle is:
     * 1. New view is added via show().
     * 2. Old view is closed.
     * 3. New view is opened.
     *
     * @class Region
     * @author Bo Gotthardt (the class)
     * @author Kim
     * @author Younes (the package name)
     */

    /**
     * Modified version of Marionette.Region.prototype.close that handles our slightly different lifecycle.
     *
     * @param {Marionette.Region} region
     * @param {Marionette.View} view
     */
    const closeView = (region, view) => {
        if (!view || view.isClosed) {
            return;
        }

        if (view.close) {
            view.close();
        } else if (view.remove) {
            view.remove();
        }

        Marionette.triggerMethod.call(region, "close");

        // Only delete the current view if it's the one being closed.
        if (region.currentView === view) {
            delete region.currentView;
        }

        // Remove the closed view's element as we no longer do that when opening the new one.
        // Note that this means that any foreign elements (i.e. not the opened or closed view) will *not* be
        // removed, unlike for the normal region that uses empty().
        view.$el.remove();
    };

    /**
     * Modified version of Marionette.Region.prototype.show that enables support for the concept of view scope.
     */
    Marionette.Region.prototype.show = function(view) {
        // Setup view scope
        ViewScope.register(this, view);

        // Following code is copied from Marionette, but since we're deferring the DOM append, the stuff that fires
        // events needed to be slightly restructured

        this.ensureEl();
        const isViewClosed = view.isClosed || _.isUndefined(view.$el);
        const isDifferentView = view !== this.currentView;
        if (isDifferentView) {
            this.close();
        }
        view.render();

        // Update state and fire events when the element is added to the DOM
        const viewOpened = () => {
            this.currentView = view;

            Marionette.triggerMethod.call(this, "show", view);
            Marionette.triggerMethod.call(view, "show");
        };

        if (isDifferentView || isViewClosed) {
            this.open(view).done(viewOpened);
        } else {
            viewOpened();
        }
    };

    Marionette.Region.prototype.close = function() {
        const view = this.currentView;

        if (view) {
            // Make sure the view knows it is being closed, so it can avoid doing expensive operations like re-rendering
            view.isClosing = true;
            Marionette.triggerMethod.call(view, "closing");

            if (view.transitions && view.transitions.out) {
                _.defer(() => {
                    view.$el.addClass(view.transitions.out);
                    view.$el.addClass("tst-animating");
                });

                view.$el.one(Browser.animationEndEventName(), () => {
                    if (view.onTransitionedOut) {
                        view.onTransitionedOut.call(view);
                    }

                    closeView(this, view);
                });
            } else {
                closeView(this, view);
            }
        }

        _.each(this.children, (view) => {
            closeView(this, view);
        });
    };

    Marionette.Region.prototype.isClosed = function() {
        return !this.currentView || this.currentView.isClosed;
    };

    /**
     * Opens the given view.
     *
     * <p>The signature differs from Marionette, in that we return a promise that will resolve when the element is
     * actually appended to the DOM. We are deferring this when animations are used to improve the experience in
     * some browsers.</p>
     *
     * @param {View} view The view to open.
     * @returns {Promise} Promise that resolves when the view is added to the DOM.
     */
    Marionette.Region.prototype.open = function(view) {
        return $.Deferred((deferred) => {
            // eslint-disable-next-line dot-notation
            if (view.transitions && view.transitions["in"]) {
                _.defer(() => {
                    // eslint-disable-next-line dot-notation
                    view.$el.addClass(view.transitions["in"]);
                    view.$el.addClass("tst-animating");
                    this.$el.append(view.el);
                    deferred.resolve();
                });

                view.$el.one(Browser.animationEndEventName(), () => {
                    // eslint-disable-next-line dot-notation
                    view.$el.removeClass(view.transitions["in"]);
                    view.$el.removeClass("tst-animating");
                    if (view.onTransitionedIn) {
                        view.onTransitionedIn.call(view);
                    }
                });
            } else {
                // Marionette uses this.$el.empty().append(view.el), but we don't want to remove the old view just yet.
                this.$el.append(view.el);
                deferred.resolve();
            }
        }).promise();
    };

    Marionette.Region.prototype.contains = function(view) {
        return this.children && this.children.indexOf(view) !== -1;
    };

    Marionette.Region.prototype.appendView = function(view) {
        ViewScope.register(this, view);

        if (_.isUndefined(this.children)) {
            this.children = [];
            this.close();
        }

        if (this.contains(view)) {
            return;
        }

        this.children.push(view);

        this.ensureEl();

        view.render();

        this.$el.append(view.el);

        Marionette.triggerMethod.call(this, "show", view);
        Marionette.triggerMethod.call(view, "show");
    };

    Marionette.Region.prototype.removeView = function(view) {
        if (this.contains(view)) {
            this.children.splice(this.children.indexOf(view), 1);

            if (view.close) {
                view.close();
            } else if (view.remove) {
                view.remove();
            }

            view.$el.remove();
        }
    };
});



// WEBPACK FOOTER //
// ./src/shared/zbone/Region.js